import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useSessionStore } from '@/shared/hooks/use-shallow-store';
import { synthPlayer } from '@/shared/lib/audio/synth-player';
import type { AudioPlayer } from '@/shared/lib/audio/types';
import { getApiBaseUrl } from '../apiBaseUrl';
import { detectPlatform } from '../detect';
import { ensureDiscordSdk, resetDiscordSdk } from '../discord/sdk';
import { getIdentityProvider } from '../identity';
import { Platform } from '../types';
import { PlatformContext, type PlatformContextValue } from './PlatformContext';

const readDiscordClientId = (): string => {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  return env?.VITE_DISCORD_CLIENT_ID ?? '';
};

/** Per-platform audio player. All platforms get the procedural synth today. */
const buildAudioPlayer = (_platform: Platform): AudioPlayer => synthPlayer;

/** Composition-root provider: detects platform, restores identity, boots Discord SDK. */
export const PlatformProvider = ({ children }: { children: ReactNode }) => {
  const setIdentity = useSessionStore((s) => s.setIdentity);
  const [discordInstanceId, setDiscordInstanceId] = useState<string | null>(null);
  const [discordSdkStatus, setDiscordSdkStatus] =
    useState<PlatformContextValue['discordSdkStatus']>('n/a');
  const [_retryNonce, setRetryNonce] = useState(0);

  const base = useMemo(() => {
    const platform = detectPlatform();
    const apiBaseUrl = getApiBaseUrl(platform);
    const identity = getIdentityProvider(platform, apiBaseUrl);
    const audio = buildAudioPlayer(platform);
    return { platform, apiBaseUrl, identity, audio };
  }, []);

  const retryDiscordSdk = useCallback(() => {
    if (base.platform !== Platform.Discord) return;
    resetDiscordSdk();
    setDiscordInstanceId(null);
    setDiscordSdkStatus('pending');
    setRetryNonce((n) => n + 1);
  }, [base.platform]);

  const value = useMemo<PlatformContextValue>(
    () => ({ ...base, discordInstanceId, discordSdkStatus, retryDiscordSdk }),
    [base, discordInstanceId, discordSdkStatus, retryDiscordSdk],
  );

  useEffect(() => {
    let cancelled = false;
    void base.identity
      .restore()
      .then((restored) => {
        if (!cancelled && restored) setIdentity(restored);
      })
      .catch(() => {
        // Best-effort: corrupt cache must not block boot.
      });
    if (base.platform === Platform.Discord) {
      setDiscordSdkStatus('pending');
      void ensureDiscordSdk(readDiscordClientId())
        .then(({ instanceId }) => {
          if (cancelled) return;
          setDiscordInstanceId(instanceId);
          setDiscordSdkStatus('ready');
        })
        .catch(() => {
          if (!cancelled) setDiscordSdkStatus('failed');
        });
    }
    return () => {
      cancelled = true;
    };
    // retryNonce in deps → retryDiscordSdk() reruns this effect.
  }, [base, setIdentity]);

  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>;
};
